import { observer } from 'mobx-react';
import { Spinner, SpinnerSize } from 'office-ui-fabric-react/lib/Spinner';
import { CardDetails } from 'owa-actionable-message';
import { getReadHostItemIndex, lazyWhenItemHasContextualAddinKeywords } from 'owa-addins-core';
import { DatapointStatus, PerformanceDatapoint } from 'owa-analytics';
import { MailboxType } from 'owa-client-ids';
import { handleOnCopy } from 'owa-content-colors';
import { LINK_HANDLER_NAME, LinkHandler } from 'owa-content-handler/lib/handlers/linkHandler';
import { getIsDarkTheme } from 'owa-fabric-theme';
import { isFeatureEnabled } from 'owa-feature-flags';
import { readItemSupportsAddins } from 'owa-mail-addins';
import getSelectedTableView from 'owa-mail-list-store/lib/utils/getSelectedTableView';
import ActionableMessageCardInItemViewState from 'owa-mail-reading-pane-store/lib/store/schema/ActionableMessageCardInItemViewState';
import readingPaneStore from 'owa-mail-reading-pane-store/lib/store/Store';
import { ClientItem } from 'owa-mail-store';
import createUserMarkupCreationHandler from 'owa-mouseup-handler/lib/handlers/userMarkupCreationHandler';
import Message from 'owa-service/lib/contract/Message';
import { lazyGetItemUserMarkupData } from 'owa-user-highlighting';
import * as React from 'react';
import {
    ActionableMessageContent,
    ActionableMessageMailWrapper,
    lazyDoesActionableMessageEnabled,
    doesItemContainCardData,
    lazyGetCardDetails,
    getActionableMessageCardMessageContextFromItem,
    lazyLogAdaptiveCardLinkInteraction,
} from 'owa-actionable-message-mail';
import {
    MailToHandler,
    MAIL_TO_HANDLER_NAME,
} from 'owa-content-handler/lib/handlers/mailToHandler';
import imageSxSHandler, {
    IMAGESXS_HANDLER_NAME,
} from 'owa-content-handler/lib/handlers/imageSxSHandler';
import {
    AdaptiveCardLinkHandler,
    ADAPTIVE_CARD_LINK_HANDLER_NAME,
} from 'owa-content-handler/lib/handlers/adaptiveCardLinkHandler';
import {
    ContextualAddinContentHandler,
    CONTEXTUAL_ADDINS_HANDLER_NAME,
} from 'owa-addins-content-handler';
import {
    cleanupImageCopyHandler,
    handleImageCopy,
} from 'owa-inline-image-loader/lib/utils/ImageCopyHandler';
import createSharingAcceptHandler, {
    SHARING_ACCEPT_HANDLER_NAME,
} from 'owa-content-handler/lib/handlers/sharingAcceptHandler';
import storeLinkHandler, {
    STORE_LINK_HANDLER_NAME,
} from 'owa-content-handler/lib/handlers/storeLinkHandler';
import createUserMarkupReadingHandler, {
    USER_MARKUP_READING_HANDLER_NAME,
} from 'owa-content-handler/lib/handlers/userMarkupReadingHandler';
import {
    DarkModeHandler,
    DARK_MODE_HANDLER_NAME,
} from 'owa-controls-content-handler/lib/handlers/darkModeHandler';
import {
    InlineImageHandler,
    INLINE_IMAGE_HANDLER_NAME,
} from 'owa-controls-content-handler/lib/handlers/inlineImageHandler';
import {
    MessageBody,
    getHitHighlightingHandler,
    HIT_HIGHLIGHTING_HANDLER_NAME,
    isConfettiEnabled,
    getConfettiHandler,
    CONFETTI_HANDLER_NAME,
} from 'owa-controls-content-handler';
import externalImageHandler, {
    EXTERNAL_IMAGE_HANDLER_NAME,
} from 'owa-content-handler/lib/handlers/externalImageHandler';
import anchorHrefHandler, {
    ANCHOR_HREF_HANDLER_NAME,
} from 'owa-content-handler/lib/handlers/anchorHrefHandler';
import externalImageCountHandler, {
    EXTERNAL_IMAGE_COUNT_HANDLER_NAME,
} from 'owa-content-handler/lib/handlers/externalImageCountHandler';

// Load context, this normally indicates surface of the loading, i.e. RP, Draft, Compose
const CONTEXT_READINGPANE = 'RP';

const styles = require('./MailMessageBody.scss');
const classNames = require('classnames/bind').bind(styles);

export interface MailMessageBodyProps {
    messageBody: string;
    copyAllowed: boolean;
    printAllowed: boolean;
    isLoading: boolean;
    undoDarkMode: boolean;
    className?: string;
    item?: ClientItem;
    actionableMessageCardInItemViewState: ActionableMessageCardInItemViewState;
}

export interface MailMessageBodyState {
    contentHandlerKeys: string[];
    isUndoModeDictionary: { [key: string]: boolean };
}

@observer
export default class MailMessageBody extends React.Component<
    MailMessageBodyProps,
    MailMessageBodyState
> {
    private readonly contentHandlerDictionary = {};
    private readonly mouseUpHandlerList = [];
    // Using the ActionableMessage enabled property under feature flag to validate its solidity
    private actionableMessageEnabled: boolean = false;
    private messageBodyWithCardPerfDatapoint: PerformanceDatapoint;

    constructor(props: MailMessageBodyProps) {
        super(props);

        this.initContentHandlers();
        this.initMouseUpHandlers();
        this.loadDoesActionableMessageEnabled();
    }

    private loadDoesActionableMessageEnabled = () => {
        lazyDoesActionableMessageEnabled.import().then(doesActionableMessageEnabled => {
            this.actionableMessageEnabled = doesActionableMessageEnabled();
        });
    };

    private addContextualAddinHandler = (keywords: string[]) => {
        const contentHandlerKeys = this.state.contentHandlerKeys;
        this.contentHandlerDictionary[
            CONTEXTUAL_ADDINS_HANDLER_NAME
        ] = new ContextualAddinContentHandler(keywords, this.props.item);
        contentHandlerKeys.push(CONTEXTUAL_ADDINS_HANDLER_NAME);
        this.setState({ contentHandlerKeys: [...contentHandlerKeys] });
    };

    private logMessageCardLinkInteractionCallback = (
        evt: MouseEvent,
        cardDetails: CardDetails
    ): void => {
        lazyLogAdaptiveCardLinkInteraction
            .import()
            .then(logAdaptiveCardLinkInteraction =>
                logAdaptiveCardLinkInteraction(
                    evt,
                    this.props.item.MailboxInfo,
                    cardDetails,
                    getActionableMessageCardMessageContextFromItem(this.props.item)
                )
            );
    };

    private initContentHandlers() {
        const { item } = this.props;

        // LinkHandler has to be before MailToHandler since they handle click events on hrefs
        // but MailToHandler prevent default on the event so LinkHandler never has a chance to handle
        // the event.
        this.contentHandlerDictionary[LINK_HANDLER_NAME] = new LinkHandler(item);
        this.contentHandlerDictionary[MAIL_TO_HANDLER_NAME] = new MailToHandler(item);
        this.contentHandlerDictionary[SHARING_ACCEPT_HANDLER_NAME] = createSharingAcceptHandler(
            item
        );
        this.contentHandlerDictionary[DARK_MODE_HANDLER_NAME] = new DarkModeHandler();

        const initialIsUndoModeDictionary: { [key: string]: boolean } = {};
        this.state = {
            contentHandlerKeys: [
                LINK_HANDLER_NAME,
                MAIL_TO_HANDLER_NAME,
                SHARING_ACCEPT_HANDLER_NAME,
            ],
            isUndoModeDictionary: initialIsUndoModeDictionary,
        };

        if (isFeatureEnabled('rp-addins') && item && readItemSupportsAddins(item)) {
            lazyWhenItemHasContextualAddinKeywords
                .import()
                .then(whenItemHasContextualAddinKeywords => {
                    whenItemHasContextualAddinKeywords(
                        getReadHostItemIndex(item.ItemId.Id),
                        this.addContextualAddinHandler
                    );
                });
        }

        if (doesItemContainCardData(item)) {
            lazyGetCardDetails.import().then(getCardDetails =>
                getCardDetails(item).then(cardDetails => {
                    if (cardDetails && cardDetails.length > 0) {
                        this.contentHandlerDictionary[
                            ADAPTIVE_CARD_LINK_HANDLER_NAME
                        ] = new AdaptiveCardLinkHandler(
                            this.logMessageCardLinkInteractionCallback,
                            cardDetails,
                            {
                                name: 'rp-acDisableTransactionCard',
                                flight: isFeatureEnabled('rp-acDisableTransactionCard'),
                            }
                        );
                        this.state.contentHandlerKeys.push(ADAPTIVE_CARD_LINK_HANDLER_NAME);
                        this.setState({ contentHandlerKeys: [...this.state.contentHandlerKeys] });
                    }
                })
            );
        }

        this.contentHandlerDictionary[INLINE_IMAGE_HANDLER_NAME] = new InlineImageHandler(
            CONTEXT_READINGPANE,
            true /* usePlaceHolder */
        );
        this.state.contentHandlerKeys.push(INLINE_IMAGE_HANDLER_NAME);

        // image sxs
        this.contentHandlerDictionary[IMAGESXS_HANDLER_NAME] = imageSxSHandler;
        this.state.contentHandlerKeys.push(IMAGESXS_HANDLER_NAME);

        const tableView = getSelectedTableView();
        // tableView could be null when RP is opened from photohub
        if (tableView?.highlightTerms) {
            this.contentHandlerDictionary[
                HIT_HIGHLIGHTING_HANDLER_NAME
            ] = getHitHighlightingHandler(tableView.highlightTerms);
            this.state.contentHandlerKeys.push(HIT_HIGHLIGHTING_HANDLER_NAME);
        }

        if (isFeatureEnabled('rp-userMarkup') && !this.isGroupItem()) {
            lazyGetItemUserMarkupData.import().then(getItemUserMarkupData => {
                getItemUserMarkupData(item).then(itemUserMarkupData => {
                    createUserMarkupReadingHandler(item, itemUserMarkupData).then(
                        userMarkupReadingHandler => {
                            this.contentHandlerDictionary[
                                USER_MARKUP_READING_HANDLER_NAME
                            ] = userMarkupReadingHandler;
                            this.state.contentHandlerKeys.push(USER_MARKUP_READING_HANDLER_NAME);
                            this.setState({
                                contentHandlerKeys: [...this.state.contentHandlerKeys],
                            });
                        }
                    );
                });
            });
        }

        this.contentHandlerDictionary[STORE_LINK_HANDLER_NAME] = storeLinkHandler;
        this.state.contentHandlerKeys.push(STORE_LINK_HANDLER_NAME);

        if (isConfettiEnabled() && item) {
            const message = item as Message;
            this.contentHandlerDictionary[CONFETTI_HANDLER_NAME] = getConfettiHandler(
                message.UniqueBody,
                !message.IsRead /* fireOnCreate */
            );
            this.state.contentHandlerKeys.push(CONFETTI_HANDLER_NAME);
        }

        this.contentHandlerDictionary[EXTERNAL_IMAGE_HANDLER_NAME] = externalImageHandler;
        this.state.contentHandlerKeys.push(EXTERNAL_IMAGE_HANDLER_NAME);

        this.contentHandlerDictionary[ANCHOR_HREF_HANDLER_NAME] = anchorHrefHandler;
        this.state.contentHandlerKeys.push(ANCHOR_HREF_HANDLER_NAME);

        this.contentHandlerDictionary[
            EXTERNAL_IMAGE_COUNT_HANDLER_NAME
        ] = externalImageCountHandler;
        this.state.contentHandlerKeys.push(EXTERNAL_IMAGE_COUNT_HANDLER_NAME);
    }

    private initMouseUpHandlers() {
        if (
            (isFeatureEnabled('mc-userHighlighting') || isFeatureEnabled('rp-userMarkup')) &&
            !this.isGroupItem()
        ) {
            this.mouseUpHandlerList.push(createUserMarkupCreationHandler(this.props.item));
        }
    }

    private renderActionableMessageCard(): JSX.Element[] {
        const { item, undoDarkMode } = this.props;

        // Checks does the item contains the Actionable MessageCard data
        const hasCardData = doesItemContainCardData(item);
        const hasActionableMessageData: boolean =
            hasCardData && !!this.props.actionableMessageCardInItemViewState;

        if (!hasActionableMessageData) {
            return null;
        }

        const elements: JSX.Element[] = [];

        // "rp-actionableMessagesV2" feature flag has a refactored version of ActionableMessage component
        // which abstracts out the component for reuse.
        if (!isFeatureEnabled('rp-actionableMessagesV2')) {
            // showCardLoading property is to represent whether to show the card loading spinner
            if (this.props.actionableMessageCardInItemViewState.showCardLoading) {
                elements.push(
                    <Spinner
                        key="loadingSpinner"
                        className={styles.adaptiveCardLoadingSpinner}
                        size={SpinnerSize.medium}
                    />
                );

                if (!this.messageBodyWithCardPerfDatapoint) {
                    this.messageBodyWithCardPerfDatapoint = new PerformanceDatapoint(
                        'RPMessageBodyWithCardData'
                    );
                }
            } else {
                if (this.messageBodyWithCardPerfDatapoint) {
                    this.messageBodyWithCardPerfDatapoint.end();
                    this.messageBodyWithCardPerfDatapoint = null;
                }
            }

            elements.push(
                <ActionableMessageContent
                    key={'headerCard'}
                    item={item}
                    viewState={this.props.actionableMessageCardInItemViewState}
                    darkModeContentHandler={
                        getIsDarkTheme() && this.contentHandlerDictionary[DARK_MODE_HANDLER_NAME]
                    }
                    undoDarkMode={undoDarkMode}
                />
            );
        } else if (this.actionableMessageEnabled) {
            // Loading refactored version of Actionable MessageCard component
            elements.push(
                <ActionableMessageMailWrapper
                    key={'headerCard'}
                    item={item}
                    viewState={this.props.actionableMessageCardInItemViewState}
                    isDarkMode={getIsDarkTheme()}
                    darkModeContentHandler={
                        getIsDarkTheme() && this.contentHandlerDictionary[DARK_MODE_HANDLER_NAME]
                    }
                    undoDarkMode={undoDarkMode}
                />
            );
        }
        return elements;
    }

    private shouldShowMessageBodyWithActionableMessageCard(): boolean {
        // showBodyWithMessageCard represents whether the Message body should be rendered along with the Actionable MessageCard
        return (
            this.props.actionableMessageCardInItemViewState &&
            this.props.actionableMessageCardInItemViewState.showBodyWithMessageCard
        );
    }

    private shouldRenderMessageBody = (elements: JSX.Element[]): boolean => {
        return elements.length === 0 || this.shouldShowMessageBodyWithActionableMessageCard();
    };

    private renderMessageBody(): JSX.Element {
        const {
            messageBody,
            copyAllowed,
            printAllowed,
            isLoading,
            className,
            item,
            undoDarkMode,
        } = this.props;

        return (
            <MessageBody
                key="messageBody"
                messageBody={messageBody}
                copyAllowed={copyAllowed}
                printAllowed={printAllowed}
                isLoading={isLoading}
                onCopy={onCopy}
                className={classNames(className, styles.mailMessageBody)}
                item={item}
                externalContentHandlerDictionary={this.contentHandlerDictionary}
                externalContentHandlerKeys={this.state.contentHandlerKeys.concat(
                    getIsDarkTheme() ? [DARK_MODE_HANDLER_NAME] : [] // Add here to trigger re-render on user toggle.
                )}
                externalIsUndoModeDictionary={{
                    ...this.state.isUndoModeDictionary,
                    [DARK_MODE_HANDLER_NAME]: undoDarkMode,
                }}
                externalMouseUpHandlerList={this.mouseUpHandlerList}
                overrideBohemiaHandler={readingPaneStore.isBohemiaEnabledOverride}
            />
        );
    }

    componentWillUnmount() {
        cleanupImageCopyHandler();

        if (this.messageBodyWithCardPerfDatapoint) {
            this.messageBodyWithCardPerfDatapoint.endWithError(DatapointStatus.RequestNotComplete);
        }
    }

    render() {
        const elements: JSX.Element[] = [];

        const cardElements: JSX.Element[] = this.renderActionableMessageCard();

        if (cardElements) {
            elements.push(...cardElements);
        }

        if (this.shouldRenderMessageBody(elements)) {
            elements.push(this.renderMessageBody());
        }

        return elements;
    }

    private isGroupItem() {
        return (
            this.props.item &&
            this.props.item.MailboxInfo &&
            this.props.item.MailboxInfo.type == MailboxType.GroupMailbox
        );
    }
}

function onCopy(evt: React.ClipboardEvent<EventTarget>) {
    handleImageCopy(evt.currentTarget as HTMLDivElement);
    handleOnCopy(evt);
}
